// src/annihilation/OldFold3D.jsx
// ============================================================
//  ANNIHILATION — THE OLD FOLD, IN 3D   (Batch 3D-ENV-1)
//  A walkable 3D home village. Built with the ie-3d-conversion
//  recipe: three.js as JSX, zero asset pipeline, every texture
//  drawn at runtime on a <canvas>, every object derived from data.
//
//  3D-ENV-1 — THE GORGEOUS WORLD PASS. The being-models recipe,
//  generalized to the environment: painted canvas textures on
//  every surface (no flat-colored boxes), signature silhouettes
//  per building (chimneys, spires, sign boards), a starred sky
//  with a moon, lamp posts + market clutter derived from the
//  geometry, villagers rebuilt from the gorgeous-pass anatomy
//  rules, and a treeline behind the hedge so the Fold sits IN a
//  world. LIGHT BUDGET UNCHANGED — every new glow is emissive +
//  toneMapped(false); not one pointLight was added. All variety
//  seeded (mulberry32), all textures cached, all scatter memo'd.
//
//  THE CONTRACT (non-negotiable): this renders the SAME village
//  as Plaza.js, from the SAME geometry (oldFoldGeometry.js), and
//  fires the SAME callbacks. It owns ONLY its own local player
//  movement + camera. It does NOT touch Firebase, auth, chat, or
//  presence — those stay in the 2D shell. The swap is one import.
//
//  Camera archetype: THE FOLLOW (walkable interior/exterior you
//  move a body through). Orbit is available but the body drives.
//  User always wins: grabbing the mouse suspends the follow goal.
//
//  Y-ELEVATION TABLE (kill z-fighting — every coplanar surface
//  gets its own tiny Y):
//     ground            0.00
//     district wash     0.02
//     path              0.05
//     cobble ring       0.06
//     doorstep pad      0.09
//     crosswalk         0.10
//  Buildings/props sit ABOVE the ground on their own footprint.
//
//  Props MIRROR Plaza's world-layer contract exactly:
//    onEnterBuilding(screen) · onEnterShop(id) · onForge()
//    onPierre() · onEnterNPC(id) · onEnterDungeon(dungeonEntry)
//    onBack()
// ============================================================

import { useRef, useMemo, useState, useEffect, useCallback } from "react";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { OrbitControls, Html } from "@react-three/drei";
import * as THREE from "three";

import {
  COLS, ROWS, SPAWN, buildingList, villagerList, portalList,
} from "./oldFoldGeometry";
import { BUILDINGS as VILLAGE_BUILDINGS, NPCS as VILLAGERS } from "./villageData";
import { levelForXp, tierForLevel } from "./stats";
// §4.18 — the shared 3D kit is the ONE home for these now. OldFold3D
// no longer carries its own copies; a beings/enemy/camera tweak is a
// one-file change in fold3dKit.js and every 3D screen gets it.
import {
  PlayerBody, TouchControls, FollowRig, Label,
  mulberry32, rnd, hashId, canvasTex, GroundBlob as KitGroundBlob, plateStyle,
} from "./fold3dKit";

// ── LAYER 1 · FOUNDATION — world constants ──────────────────
// UNIT is the single most important number in the file: world
// units per grid step. Tune it LAST. The Old Fold reads as a
// courtyard, so a mid UNIT keeps buildings apart without sprawl.
const UNIT     = 3.0;
const INK      = "#141019";   // obsidian — the game's border ink (theme v4)
const VELLUM   = "#e9ddc4";   // moonvellum text
const GOLD      = "#c9a84c";
const GOLD_LIT  = "#f0d060";
const GROUND_C  = "#2a2416";   // dark earth
const GRASS_C   = "#3d4a24";
const WALK_C    = "#5b4a30";   // dirt path
const COBBLE_C  = "#4a4238";

// Elevation table (see header)
const Y_GROUND   = 0.00;
const Y_WASH     = 0.02;
const Y_PATH     = 0.05;
const Y_COBBLE   = 0.06;
const Y_PAD      = 0.09;

// Grid → world. Grid origin is top-left; center the whole map on
// the origin so orbit/spawn feel natural. Grid +y → world +z.
const HALF_X = (COLS * UNIT) / 2;
const HALF_Z = (ROWS * UNIT) / 2;
const worldPos = (gx, gy) => [gx * UNIT - HALF_X, 0, gy * UNIT - HALF_Z];

// ── Display-name join — geometry file has ids, villageData has
// names. One deriving lookup, no parallel copies.
const BUILD_NAME = Object.fromEntries(VILLAGE_BUILDINGS.map(b => [b.id, b.name]));
const BUILD_SCREEN = Object.fromEntries(VILLAGE_BUILDINGS.map(b => [b.id, b.screen]));
const NPC_NAME = Object.fromEntries(VILLAGERS.map(n => [n.id, n.name]));

// ── tiny color helpers (display-only; canon palettes untouched) ─
function shade(hex, amt) {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.max(0, Math.min(255, (n >> 16) + amt));
  const gg = Math.max(0, Math.min(255, ((n >> 8) & 255) + amt));
  const b = Math.max(0, Math.min(255, (n & 255) + amt));
  return `rgb(${r},${gg},${b})`;
}
function hexA(hex, a) {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${n >> 16},${(n >> 8) & 255},${n & 255},${a})`;
}

// ────────────────────────────────────────────────────────────
// PAINTED TEXTURES (3D-ENV-1) — the coat-painting move from the
// being recipe, applied to the world. One cached canvas per
// surface family; seeded mottle; signature marks. Never flat.
// ────────────────────────────────────────────────────────────

// Ground — tilled dark earth: radial footworn gradient, seeded
// soil mottle, moss blooms, half-buried pebbles, faint furrows.
function groundTexture() {
  return canvasTex("ground_env1", 512, 512, (g, w, h) => {
    const grd = g.createRadialGradient(w / 2, h / 2, 40, w / 2, h / 2, w * 0.7);
    grd.addColorStop(0, "#332b1a");    // footworn heart of the square
    grd.addColorStop(0.6, GROUND_C);
    grd.addColorStop(1, "#221c10");    // darker at the hedge line
    g.fillStyle = grd; g.fillRect(0, 0, w, h);
    const R = mulberry32(99);
    // soil mottle
    for (let i = 0; i < 2600; i++) {
      const x = R() * w, y = R() * h, a = 0.04 + R() * 0.08;
      g.fillStyle = `rgba(${R() > 0.5 ? "72,60,36" : "18,14,8"},${a})`;
      g.fillRect(x, y, 2 + R() * 3, 2 + R() * 3);
    }
    // moss blooms
    for (let i = 0; i < 46; i++) {
      const x = R() * w, y = R() * h, r = 8 + R() * 22;
      const m = g.createRadialGradient(x, y, 1, x, y, r);
      m.addColorStop(0, `rgba(61,74,36,${0.16 + R() * 0.14})`);
      m.addColorStop(1, "rgba(61,74,36,0)");
      g.fillStyle = m; g.beginPath(); g.arc(x, y, r, 0, 7); g.fill();
    }
    // half-buried pebbles with cast shadow
    for (let i = 0; i < 90; i++) {
      const x = R() * w, y = R() * h, r = 1.5 + R() * 3;
      g.fillStyle = "rgba(0,0,0,0.25)";
      g.beginPath(); g.ellipse(x + r * 0.3, y + r * 0.4, r, r * 0.5, R() * 3, 0, 7); g.fill();
      g.fillStyle = `rgba(${100 + R() * 40},${92 + R() * 30},${80 + R() * 20},0.5)`;
      g.beginPath(); g.ellipse(x, y, r, r * 0.7, R() * 3, 0, 7); g.fill();
    }
    // faint furrow grid — kept from Ship 1, quieter now
    g.strokeStyle = "rgba(0,0,0,0.12)"; g.lineWidth = 1;
    for (let i = 0; i <= 8; i++) {
      g.beginPath(); g.moveTo((i / 8) * w, 0); g.lineTo((i / 8) * w, h); g.stroke();
      g.beginPath(); g.moveTo(0, (i / 8) * h); g.lineTo(w, (i / 8) * h); g.stroke();
    }
  });
}

// Dirt path — packed earth with wheel-worn ruts and grassy edges.
function pathTexture() {
  return canvasTex("path_env1", 128, 256, (g, w, h) => {
    g.fillStyle = WALK_C; g.fillRect(0, 0, w, h);
    const R = mulberry32(41);
    for (let i = 0; i < 500; i++) {
      g.fillStyle = R() > 0.5 ? "rgba(30,22,12,0.3)" : "rgba(120,98,64,0.22)";
      g.fillRect(R() * w, R() * h, 2 + R() * 3, 2 + R() * 4);
    }
    // two worn ruts down the length
    for (const rx of [w * 0.3, w * 0.7]) {
      const rut = g.createLinearGradient(rx - 8, 0, rx + 8, 0);
      rut.addColorStop(0, "rgba(0,0,0,0)");
      rut.addColorStop(0.5, "rgba(0,0,0,0.22)");
      rut.addColorStop(1, "rgba(0,0,0,0)");
      g.fillStyle = rut; g.fillRect(rx - 8, 0, 16, h);
    }
    // ragged edges — grass creeping in
    for (let i = 0; i < 60; i++) {
      const y = R() * h, side = R() > 0.5;
      g.fillStyle = `rgba(50,62,30,${0.3 + R() * 0.3})`;
      g.beginPath(); g.ellipse(side ? R() * 8 : w - R() * 8, y, 3 + R() * 5, 2 + R() * 3, 0, 0, 7); g.fill();
    }
  });
}

// Cobble ring — real fitted stones with mortar shadow and moss.
function cobbleTexture() {
  return canvasTex("cobble_env1", 256, 256, (g, w, h) => {
    g.fillStyle = "#37322a"; g.fillRect(0, 0, w, h);
    const R = mulberry32(77);
    const CS = 22;
    for (let ry = 0; ry < h / CS + 1; ry++) {
      for (let cx = 0; cx < w / CS + 1; cx++) {
        const ox = (ry % 2) * CS * 0.5;
        const x = cx * CS + ox + (R() - 0.5) * 3, y = ry * CS + (R() - 0.5) * 3;
        const rw = CS * (0.72 + R() * 0.2), rh = CS * (0.6 + R() * 0.2);
        const tone = 62 + R() * 26;
        g.fillStyle = `rgb(${tone + 8},${tone},${tone - 8})`;
        g.beginPath();
        if (g.roundRect) g.roundRect(x, y, rw, rh, 6); else g.rect(x, y, rw, rh);
        g.fill();
        g.strokeStyle = "rgba(0,0,0,0.45)"; g.lineWidth = 2; g.stroke();
        if (R() > 0.72) { // mossy joints
          g.fillStyle = "rgba(58,74,36,0.35)";
          g.beginPath(); g.arc(x + R() * rw, y + R() * rh, 2 + R() * 3, 0, 7); g.fill();
        }
      }
    }
  });
}

// Facade texture per building style — the big one. Stone footing,
// timber-framed plaster upper storeys, mullioned windows (the
// emissiveMap = map trick makes the lit panes actually glow), a
// carved plank door with iron hinges, awning, door-lamp bleed,
// weathering streaks. Seeded per building id.
const STYLE = {
  bonfire:     { wall: "#3a2a1a", trim: "#e8a33d", lit: 0.0,  glow: "#ffb24d", floors: 2 },
  marketplace: { wall: "#4a3f24", trim: "#c9a84c", lit: 0.45, glow: "#ffe08a", floors: 2 },
  forge:       { wall: "#3a2420", trim: "#f0946a", lit: 0.55, glow: "#ff7a3a", floors: 2 },
  trainer:     { wall: "#23303c", trim: "#8fb8d8", lit: 0.35, glow: "#bfe0ff", floors: 3 },
  registrar:   { wall: "#2c2440", trim: "#b8a0e8", lit: 0.4,  glow: "#d8c0ff", floors: 3 },
  bulletin:    { wall: "#2e3a24", trim: "#a8c686", lit: 0.3,  glow: "#d8f0b0", floors: 1 },
  garden:      { wall: "#1c3a30", trim: "#7fe8c8", lit: 0.25, glow: "#b8fff0", floors: 1 },
};
const styleOf = (id) => STYLE[id] || STYLE.marketplace;   // || fallback = future-proof

function facadeTexture(id) {
  const s = styleOf(id);
  const key = `facade_env1|${id}`;
  const H = Math.max(192, s.floors * 80);
  return canvasTex(key, 256, H, (g, w, h) => {
    const R = mulberry32(hashId(id) + 13);
    // plaster base with vertical tone drift
    const base = g.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, s.wall);
    base.addColorStop(1, shade(s.wall, -18));
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    // plaster mottle
    for (let i = 0; i < 700; i++) {
      g.fillStyle = `rgba(${R() > 0.5 ? "255,255,255" : "0,0,0"},${0.02 + R() * 0.04})`;
      g.fillRect(R() * w, R() * h, 3 + R() * 5, 2 + R() * 4);
    }
    // stone footing course (bottom band)
    const footH = h * 0.16;
    g.fillStyle = "#3a3630"; g.fillRect(0, h - footH, w, footH);
    for (let i = 0; i < 9; i++) {
      const x = (i / 9) * w + (R() - 0.5) * 4;
      const sw = w / 9 * (0.8 + R() * 0.16);
      const tone = 58 + R() * 22;
      g.fillStyle = `rgb(${tone},${tone - 2},${tone - 6})`;
      g.fillRect(x + 2, h - footH + 3, sw - 4, footH - 6);
      g.strokeStyle = "rgba(0,0,0,0.5)"; g.lineWidth = 2;
      g.strokeRect(x + 2, h - footH + 3, sw - 4, footH - 6);
    }
    // timber frame — dark beams over the plaster
    const beam = "#241a12";
    g.fillStyle = beam;
    g.fillRect(0, 0, 8, h - footH); g.fillRect(w - 8, 0, 8, h - footH);
    const rows = Math.max(1, s.floors);
    const bandT = 8;
    for (let r = 0; r <= rows; r++) {
      const y = (r / rows) * (h - footH - 12);
      g.fillRect(0, y, w, bandT);
    }
    // diagonal braces in the top band
    g.save(); g.strokeStyle = beam; g.lineWidth = 7;
    g.beginPath(); g.moveTo(10, bandT); g.lineTo(w * 0.35, (h - footH) / rows); g.stroke();
    g.beginPath(); g.moveTo(w - 10, bandT); g.lineTo(w * 0.65, (h - footH) / rows); g.stroke();
    g.restore();
    // window grid, upper floors — mullioned, warm-lit panes
    const cols = 3;
    const mgx = 26, gw = (w - mgx * 2) / cols, gh = (h - footH - 24) / rows;
    for (let r = 0; r < rows; r++) {
      for (let cc = 0; cc < cols; cc++) {
        const on = R() < s.lit;
        const x = mgx + cc * gw + gw * 0.2, y = 14 + r * gh + gh * 0.2;
        const ww = gw * 0.6, hh = gh * 0.56;
        // frame
        g.fillStyle = beam; g.fillRect(x - 4, y - 4, ww + 8, hh + 8);
        // panes (glowing gradient when lit)
        if (on) {
          const lg = g.createRadialGradient(x + ww / 2, y + hh / 2, 2, x + ww / 2, y + hh / 2, ww);
          lg.addColorStop(0, "#fff2c8");
          lg.addColorStop(0.5, s.glow);
          lg.addColorStop(1, shade(s.glow, -40));
          g.fillStyle = lg;
        } else {
          g.fillStyle = "#0c0a10";
        }
        g.fillRect(x, y, ww, hh);
        // mullions
        g.strokeStyle = beam; g.lineWidth = 3;
        g.beginPath(); g.moveTo(x + ww / 2, y); g.lineTo(x + ww / 2, y + hh); g.stroke();
        g.beginPath(); g.moveTo(x, y + hh / 2); g.lineTo(x + ww, y + hh / 2); g.stroke();
        // sill shadow
        g.fillStyle = "rgba(0,0,0,0.35)"; g.fillRect(x - 5, y + hh + 4, ww + 10, 3);
        if (!on && R() > 0.6) { // faint moonlight sheen on dark panes
          g.fillStyle = "rgba(140,160,200,0.12)";
          g.beginPath(); g.moveTo(x, y); g.lineTo(x + ww * 0.5, y); g.lineTo(x, y + hh * 0.6); g.closePath(); g.fill();
        }
      }
    }
    // ground-floor storefront band + carved door + iron hinges
    g.fillStyle = "rgba(0,0,0,0.35)"; g.fillRect(0, h - footH - 44, w, 44);
    g.fillStyle = s.trim; g.fillRect(w / 2 - 22, h - footH - 42, 44, 42);
    g.fillStyle = "#120c08"; g.fillRect(w / 2 - 17, h - footH - 37, 34, 37);
    g.strokeStyle = "rgba(255,255,255,0.06)"; g.lineWidth = 2;
    for (let i = 1; i < 4; i++) { g.beginPath(); g.moveTo(w / 2 - 17 + i * 8.5, h - footH - 37); g.lineTo(w / 2 - 17 + i * 8.5, h - footH); g.stroke(); }
    g.fillStyle = "#4a4640";
    g.fillRect(w / 2 - 16, h - footH - 32, 12, 3); g.fillRect(w / 2 - 16, h - footH - 12, 12, 3);
    // door lamp glow bleed onto the wall
    const dg = g.createRadialGradient(w / 2, h - footH - 40, 2, w / 2, h - footH - 40, 46);
    dg.addColorStop(0, hexA(s.glow, 0.35)); dg.addColorStop(1, hexA(s.glow, 0));
    g.fillStyle = dg; g.beginPath(); g.arc(w / 2, h - footH - 40, 46, 0, 7); g.fill();
    // awning stripe
    g.fillStyle = s.trim; g.fillRect(0, h - footH - 52, w, 8);
    g.fillStyle = "rgba(0,0,0,0.3)"; g.fillRect(0, h - footH - 45, w, 3);
    // weathering streaks
    for (let i = 0; i < 12; i++) {
      const x = R() * w;
      g.fillStyle = "rgba(0,0,0,0.1)";
      g.fillRect(x, R() * h * 0.5, 2, 20 + R() * 40);
    }
  });
}

// Wood-shingle roof texture — overlapping courses, seeded tone.
function roofTexture() {
  return canvasTex("roof_env1", 128, 128, (g, w, h) => {
    g.fillStyle = "#191420"; g.fillRect(0, 0, w, h);
    const R = mulberry32(55);
    const SH = 14;
    for (let ry = 0; ry < h / SH + 1; ry++) {
      const ox = (ry % 2) * 10;
      for (let x = -20; x < w; x += 20) {
        const tone = 22 + R() * 16;
        g.fillStyle = `rgb(${tone + 6},${tone},${tone + 10})`;
        g.beginPath();
        if (g.roundRect) g.roundRect(x + ox, ry * SH, 18, SH + 3, 3); else g.rect(x + ox, ry * SH, 18, SH + 3);
        g.fill();
        g.strokeStyle = "rgba(0,0,0,0.5)"; g.lineWidth = 1.5; g.stroke();
      }
    }
  });
}

// Hedge — dense painted leaf mass for the perimeter wall.
function hedgeTexture() {
  return canvasTex("hedge_env1", 256, 128, (g, w, h) => {
    g.fillStyle = "#1c2816"; g.fillRect(0, 0, w, h);
    const R = mulberry32(33);
    for (let i = 0; i < 1400; i++) {
      const x = R() * w, y = R() * h, r = 2 + R() * 4;
      const gr = 46 + R() * 40;
      g.fillStyle = `rgba(${gr * 0.55},${gr},${gr * 0.4},${0.35 + R() * 0.4})`;
      g.beginPath(); g.ellipse(x, y, r, r * 0.7, R() * 3, 0, 7); g.fill();
    }
    // moonlit top edge
    const tg = g.createLinearGradient(0, 0, 0, h * 0.4);
    tg.addColorStop(0, "rgba(150,180,140,0.18)"); tg.addColorStop(1, "rgba(150,180,140,0)");
    g.fillStyle = tg; g.fillRect(0, 0, w, h * 0.4);
  });
}

// Bark + canopy for the perimeter treeline (grove kin, at a distance)
function barkTexture() {
  return canvasTex("fold_bark_env1", 64, 128, (g, w, h) => {
    g.fillStyle = "#d8d0c0"; g.fillRect(0, 0, w, h);
    const R = mulberry32(29);
    for (let i = 0; i < 26; i++) { // birch lenticels
      g.fillStyle = `rgba(30,26,20,${0.5 + R() * 0.4})`;
      g.beginPath(); g.ellipse(R() * w, R() * h, 4 + R() * 8, 1.5 + R() * 2, 0, 0, 7); g.fill();
    }
    for (let i = 0; i < 120; i++) {
      g.fillStyle = `rgba(0,0,0,${0.04 + R() * 0.08})`;
      g.fillRect(R() * w, R() * h, 1.5, 3 + R() * 6);
    }
  });
}
function canopyTexture() {
  return canvasTex("fold_canopy_env1", 128, 128, (g, w, h) => {
    g.fillStyle = "#28381c"; g.fillRect(0, 0, w, h);
    const R = mulberry32(31);
    for (let i = 0; i < 900; i++) {
      const gr = 50 + R() * 46;
      g.fillStyle = `rgba(${gr * 0.6},${gr},${gr * 0.42},${0.3 + R() * 0.4})`;
      g.beginPath(); g.arc(R() * w, R() * h, 2 + R() * 4, 0, 7); g.fill();
    }
  });
}

// Villager robe texture — painted cloth: gradient body, seeded
// weave grain, vertical fold shadows, hem trim. Cached per tone.
function robeTexture(tone) {
  return canvasTex(`robe_env1|${tone}`, 128, 128, (g, w, h) => {
    const grd = g.createLinearGradient(0, 0, 0, h);
    grd.addColorStop(0, shade(tone, 14));
    grd.addColorStop(0.7, tone);
    grd.addColorStop(1, shade(tone, -30));
    g.fillStyle = grd; g.fillRect(0, 0, w, h);
    const R = mulberry32(hashId(tone) + 3);
    for (let i = 0; i < 300; i++) { // weave grain
      g.fillStyle = `rgba(${R() > 0.5 ? "255,255,255" : "0,0,0"},${0.03 + R() * 0.04})`;
      g.fillRect(R() * w, R() * h, 1.5, 3 + R() * 5);
    }
    // vertical fold shadows
    for (let i = 0; i < 5; i++) {
      const x = (i + 0.5) * (w / 5) + (R() - 0.5) * 6;
      const fg = g.createLinearGradient(x - 5, 0, x + 5, 0);
      fg.addColorStop(0, "rgba(0,0,0,0)"); fg.addColorStop(0.5, "rgba(0,0,0,0.2)"); fg.addColorStop(1, "rgba(0,0,0,0)");
      g.fillStyle = fg; g.fillRect(x - 5, h * 0.2, 10, h * 0.8);
    }
    // hem trim band
    g.fillStyle = "rgba(20,16,25,0.8)"; g.fillRect(0, h - 12, w, 12);
    g.fillStyle = hexA("#c9a84c", 0.6); g.fillRect(0, h - 14, w, 2);
  });
}

// ────────────────────────────────────────────────────────────
// LAYER 2 · ENVIRONMENT
// ────────────────────────────────────────────────────────────
function skyTexture() {
  return canvasTex("sky_env1", 512, 256, (g, w, h) => {
    const grd = g.createLinearGradient(0, 0, 0, h);
    grd.addColorStop(0.0, "#0c0a18");   // deep night at the top
    grd.addColorStop(0.55, "#241a3a");  // violet
    grd.addColorStop(0.82, "#4a2f3a");  // warm horizon
    grd.addColorStop(1.0, "#5a3a2a");
    g.fillStyle = grd; g.fillRect(0, 0, w, h);
    const R = mulberry32(452);
    // a faint chao-nebula band across the upper sky
    for (let i = 0; i < 40; i++) {
      const x = R() * w, y = R() * h * 0.45, r = 20 + R() * 60;
      const nb = g.createRadialGradient(x, y, 2, x, y, r);
      nb.addColorStop(0, `rgba(120,100,180,${0.03 + R() * 0.04})`);
      nb.addColorStop(1, "rgba(120,100,180,0)");
      g.fillStyle = nb; g.beginPath(); g.arc(x, y, r, 0, 7); g.fill();
    }
    // stars — dense high, sparse toward the horizon; a few burn bright
    for (let i = 0; i < 480; i++) {
      const y = Math.pow(R(), 1.8) * h * 0.72;
      const x = R() * w;
      const big = R() > 0.965;
      const a = big ? 0.9 : 0.25 + R() * 0.45;
      g.fillStyle = `rgba(${big ? "255,244,214" : "220,224,255"},${a})`;
      const st = big ? 2.4 : 0.8 + R() * 1.1;
      g.fillRect(x, y, st, st);
      if (big) { // four-point twinkle
        g.fillStyle = "rgba(255,244,214,0.35)";
        g.fillRect(x - 2.5, y + st / 2 - 0.4, 7, 0.8);
        g.fillRect(x + st / 2 - 0.4, y - 2.5, 0.8, 7);
      }
    }
  });
}

function Sky() {
  const tex = useMemo(skyTexture, []);
  return (
    <group>
      <mesh scale={[-1, 1, 1]}>
        <sphereGeometry args={[220, 24, 16]} />
        <meshBasicMaterial map={tex} side={THREE.BackSide} fog={false} depthWrite={false} />
      </mesh>
      {/* the moon — a lit disc with a soft halo. Emissive, no light. */}
      <group position={[-90, 110, -150]}>
        <mesh>
          <sphereGeometry args={[9, 20, 20]} />
          <meshBasicMaterial color="#e8e2d0" fog={false} toneMapped={false} />
        </mesh>
        <mesh>
          <sphereGeometry args={[14, 20, 20]} />
          <meshBasicMaterial color="#c8c0e0" transparent opacity={0.12} fog={false} depthWrite={false} toneMapped={false} />
        </mesh>
      </group>
    </group>
  );
}

function Ground() {
  const tex = useMemo(groundTexture, []);
  useMemo(() => { tex.wrapS = tex.wrapT = THREE.RepeatWrapping; tex.repeat.set(COLS / 4, ROWS / 4); }, [tex]);
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, Y_GROUND, 0]} receiveShadow>
      <planeGeometry args={[COLS * UNIT + 12, ROWS * UNIT + 12]} />
      <meshStandardMaterial map={tex} roughness={1} />
    </mesh>
  );
}

// Grass — tuft CLUSTERS now (3 blades each, seeded lean) with the
// occasional chao-flower: a tiny emissive bud that costs no light.
function Grass() {
  const tufts = useMemo(() => {
    const R = mulberry32(7);
    const out = [];
    for (let i = 0; i < 90; i++) {
      const gx = R() * COLS, gy = R() * ROWS;
      const [x, , z] = worldPos(gx, gy);
      out.push({ x, z, s: 0.4 + R() * 0.5, k: i, flower: R() > 0.86, lean: (R() - 0.5) * 0.5 });
    }
    return out;
  }, []);
  return (
    <group>
      {tufts.map((b) => (
        <group key={b.k} position={[b.x, 0, b.z]} rotation={[0, b.k * 1.7, 0]}>
          <mesh position={[0, 0.18 * b.s, 0]} rotation={[b.lean * 0.4, 0, b.lean]}>
            <coneGeometry args={[0.1 * b.s, 0.44 * b.s, 4]} />
            <meshStandardMaterial color={GRASS_C} roughness={1} />
          </mesh>
          <mesh position={[0.09 * b.s, 0.14 * b.s, 0.04]} rotation={[0, 1.2, -b.lean]}>
            <coneGeometry args={[0.08 * b.s, 0.34 * b.s, 4]} />
            <meshStandardMaterial color="#4a5a2c" roughness={1} />
          </mesh>
          <mesh position={[-0.08 * b.s, 0.12 * b.s, -0.04]} rotation={[b.lean, 2.4, 0.2]}>
            <coneGeometry args={[0.07 * b.s, 0.3 * b.s, 4]} />
            <meshStandardMaterial color="#36421f" roughness={1} />
          </mesh>
          {b.flower && (
            <mesh position={[0.04, 0.34 * b.s, 0]}>
              <sphereGeometry args={[0.05, 6, 6]} />
              <meshBasicMaterial color="#9fe8d0" toneMapped={false} />
            </mesh>
          )}
        </group>
      ))}
    </group>
  );
}

// Perimeter — a living hedge now: painted leaf texture, irregular
// crown lumps riding the wall line, and a birch treeline standing
// BEHIND it so the village has a skyline instead of an edge.
function Perimeter() {
  const hedgeTex = useMemo(hedgeTexture, []);
  useMemo(() => { hedgeTex.wrapS = hedgeTex.wrapT = THREE.RepeatWrapping; hedgeTex.repeat.set(8, 1); }, [hedgeTex]);
  const segs = useMemo(() => {
    const W = COLS * UNIT + 8, D = ROWS * UNIT + 8;
    return [
      { x: 0, z: -D / 2, w: W, d: 1.2, k: "n" },
      { x: 0, z: D / 2, w: W, d: 1.2, k: "s" },
      { x: -W / 2, z: 0, w: 1.2, d: D, k: "w" },
      { x: W / 2, z: 0, w: 1.2, d: D, k: "e" },
    ];
  }, []);
  // crown lumps riding each segment (seeded)
  const lumps = useMemo(() => {
    const R = mulberry32(83);
    const out = [];
    segs.forEach((s, si) => {
      const along = Math.max(s.w, s.d);
      const n = Math.floor(along / 4);
      for (let i = 0; i < n; i++) {
        const t = (i + 0.5) / n - 0.5;
        out.push({
          x: s.x + (s.w > s.d ? t * s.w : (R() - 0.5) * 0.5),
          z: s.z + (s.d > s.w ? t * s.d : (R() - 0.5) * 0.5),
          r: 0.9 + R() * 0.7, y: 1.7 + R() * 0.5, k: `${si}-${i}`,
        });
      }
    });
    return out;
  }, [segs]);
  // treeline behind the hedge
  const barkTex = useMemo(barkTexture, []);
  const canTex = useMemo(canopyTexture, []);
  const trees = useMemo(() => {
    const R = mulberry32(91);
    const W = COLS * UNIT + 8, D = ROWS * UNIT + 8;
    const out = [];
    for (let i = 0; i < 34; i++) {
      const side = i % 4;
      const t = R() - 0.5;
      const off = 3 + R() * 6;
      let x, z;
      if (side === 0) { x = t * W; z = -D / 2 - off; }
      else if (side === 1) { x = t * W; z = D / 2 + off; }
      else if (side === 2) { x = -W / 2 - off; z = t * D; }
      else { x = W / 2 + off; z = t * D; }
      out.push({ x, z, h: 5 + R() * 4, r: 1.4 + R() * 0.9, lean: (R() - 0.5) * 0.1, k: i });
    }
    return out;
  }, []);
  return (
    <group>
      {segs.map((s) => (
        <mesh key={s.k} position={[s.x, 0.9, s.z]} castShadow receiveShadow>
          <boxGeometry args={[s.w, 1.8, s.d]} />
          <meshStandardMaterial map={hedgeTex} roughness={1} />
        </mesh>
      ))}
      {lumps.map((l) => (
        <mesh key={l.k} position={[l.x, l.y, l.z]}>
          <sphereGeometry args={[l.r, 7, 6]} />
          <meshStandardMaterial map={hedgeTex} roughness={1} />
        </mesh>
      ))}
      {trees.map((t) => (
        <group key={t.k} position={[t.x, 0, t.z]} rotation={[t.lean, t.k * 2.1, 0]}>
          <mesh position={[0, t.h / 2, 0]}>
            <cylinderGeometry args={[0.16, 0.24, t.h, 7]} />
            <meshStandardMaterial map={barkTex} roughness={1} />
          </mesh>
          <mesh position={[0, t.h + t.r * 0.5, 0]}>
            <sphereGeometry args={[t.r, 8, 7]} />
            <meshStandardMaterial map={canTex} roughness={1} />
          </mesh>
          <mesh position={[t.r * 0.4, t.h + t.r * 0.9, t.r * 0.3]}>
            <sphereGeometry args={[t.r * 0.6, 7, 6]} />
            <meshStandardMaterial map={canTex} roughness={1} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

// Drifting Chao motes — the cheapest motion in the file, and it
// sells "alive." A few dozen tiny glowing spheres rising and wrapping.
function Motes() {
  const ref = useRef();
  const data = useMemo(() => {
    const R = mulberry32(451);
    return Array.from({ length: 40 }, (_, i) => ({
      x: (R() - 0.5) * COLS * UNIT,
      z: (R() - 0.5) * ROWS * UNIT,
      y: R() * 8,
      spd: 0.3 + R() * 0.5,
      k: i,
    }));
  }, []);
  useFrame((_, dt) => {
    if (!ref.current) return;
    ref.current.children.forEach((m, i) => {
      const d = data[i];
      m.position.y += d.spd * dt;
      if (m.position.y > 9) m.position.y = 0;
      m.material.opacity = 0.2 + 0.5 * Math.sin(m.position.y * 0.6 + i);
    });
  });
  return (
    <group ref={ref}>
      {data.map((d) => (
        <mesh key={d.k} position={[d.x, d.y, d.z]}>
          <sphereGeometry args={[0.08, 6, 6]} />
          <meshBasicMaterial color="#9fe8d0" transparent opacity={0.5} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// LAYER 3 · PATHS — dirt paths radiating from the Bonfire (center)
// to every building. Derived from geometry, never hand-placed.
// ────────────────────────────────────────────────────────────
function Paths({ buildings }) {
  const tex = useMemo(pathTexture, []);
  const segs = useMemo(() => {
    const [cx, , cz] = worldPos(SPAWN.x, SPAWN.y);
    return buildings.map((b) => {
      const [bx, , bz] = worldPos(b.x, b.y);
      const len = Math.hypot(bx - cx, bz - cz);
      return {
        len, mx: (cx + bx) / 2, mz: (cz + bz) / 2,
        ang: Math.atan2(bz - cz, bx - cx), k: b.id,
      };
    });
  }, [buildings]);
  return (
    <group>
      {segs.map((s) => (
        <mesh key={s.k} rotation={[-Math.PI / 2, 0, -s.ang]} position={[s.mx, Y_PATH, s.mz]} receiveShadow>
          <planeGeometry args={[s.len, 1.4]} />
          <meshStandardMaterial map={tex} roughness={1} />
        </mesh>
      ))}
    </group>
  );
}

// Lamp posts standing where each path meets the square — derived
// from the same segment math, seeded lantern sway phase. The lamp
// head is EMISSIVE (toneMapped false): it reads lit with no light.
function LampPosts({ buildings }) {
  const posts = useMemo(() => {
    const [cx, , cz] = worldPos(SPAWN.x, SPAWN.y);
    return buildings
      .filter((b) => b.id !== "bonfire")
      .map((b) => {
        const [bx, , bz] = worldPos(b.x, b.y);
        const ang = Math.atan2(bz - cz, bx - cx);
        const r = UNIT * 3.1; // just outside the cobble ring
        return { x: cx + Math.cos(ang) * r, z: cz + Math.sin(ang) * r, k: b.id, ph: hashId(b.id) % 7 };
      });
  }, [buildings]);
  const grp = useRef();
  useFrame((s) => {
    if (!grp.current) return;
    grp.current.children.forEach((post, i) => {
      const head = post.children[3];
      if (head) head.rotation.z = Math.sin(s.clock.elapsedTime * 1.4 + posts[i].ph) * 0.06;
    });
  });
  return (
    <group ref={grp}>
      {posts.map((p) => (
        <group key={p.k} position={[p.x, 0, p.z]}>
          <mesh position={[0, 0.1, 0]}><cylinderGeometry args={[0.16, 0.2, 0.2, 8]} /><meshStandardMaterial color="#3a342c" roughness={1} /></mesh>
          <mesh position={[0, 1.3, 0]} castShadow><cylinderGeometry args={[0.05, 0.07, 2.4, 6]} /><meshStandardMaterial color="#241a12" roughness={1} /></mesh>
          <mesh position={[0.22, 2.5, 0]} rotation={[0, 0, -0.5]}><cylinderGeometry args={[0.035, 0.035, 0.55, 5]} /><meshStandardMaterial color="#241a12" roughness={1} /></mesh>
          <group position={[0.42, 2.36, 0]}>
            <mesh><boxGeometry args={[0.2, 0.26, 0.2]} /><meshStandardMaterial color="#241a12" roughness={1} /></mesh>
            <mesh><boxGeometry args={[0.13, 0.18, 0.13]} /><meshBasicMaterial color="#ffd88a" toneMapped={false} /></mesh>
            <mesh position={[0, 0.17, 0]}><coneGeometry args={[0.15, 0.12, 4]} /><meshStandardMaterial color="#141019" /></mesh>
          </group>
        </group>
      ))}
    </group>
  );
}

// Cobblestone ring around the Bonfire square — fitted stones now.
function CobbleRing() {
  const tex = useMemo(cobbleTexture, []);
  useMemo(() => { tex.wrapS = tex.wrapT = THREE.RepeatWrapping; tex.repeat.set(3, 3); }, [tex]);
  const [cx, , cz] = worldPos(SPAWN.x, SPAWN.y);
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[cx, Y_COBBLE, cz]} receiveShadow>
      <circleGeometry args={[UNIT * 2.4, 32]} />
      <meshStandardMaterial map={tex} roughness={1} />
    </mesh>
  );
}

// ────────────────────────────────────────────────────────────
// LAYER 4 · OBJECTS — one 3D structure per building row.
// Height, roof, and detail derived from the style table + seed.
// The Bonfire gets special treatment (it's the heart of the Fold).
// ────────────────────────────────────────────────────────────
function heightOf(id) {
  const s = styleOf(id);
  const base = 2.2 + s.floors * 1.1;
  const r = rnd(hashId(id) + 5)();
  return base + r * 1.2;
}

function Building({ b, onClick }) {
  const s = styleOf(b.id);
  const [x, , z] = worldPos(b.x, b.y);
  const h = heightOf(b.id);
  const w = (b.rx || 2) * UNIT * 0.9;
  const d = (b.ry || 2) * UNIT * 0.9;
  const facade = useMemo(() => facadeTexture(b.id), [b.id]);
  const roofTex = useMemo(roofTexture, []);
  const [hover, setHover] = useState(false);
  const name = BUILD_NAME[b.id] || b.id;
  const chimneySide = useMemo(() => (rnd(hashId(b.id) + 21)() > 0.5 ? 1 : -1), [b.id]);

  // The Bonfire is not a building — it's a stone fire ring.
  if (b.id === "bonfire") return <Bonfire x={x} z={z} onClick={onClick} name={name} />;

  return (
    <group position={[x, 0, z]}>
      {/* stone plinth the whole building stands on */}
      <mesh position={[0, 0.14, 0]} receiveShadow>
        <boxGeometry args={[w + 0.5, 0.28, d + 0.5]} />
        <meshStandardMaterial color="#3a3630" roughness={1} />
      </mesh>
      {/* body — timber-framed painted facade, lit windows glowing */}
      <mesh
        position={[0, h / 2, 0]} castShadow receiveShadow
        onPointerOver={(e) => { e.stopPropagation(); setHover(true); document.body.style.cursor = "pointer"; }}
        onPointerOut={() => { setHover(false); document.body.style.cursor = "auto"; }}
        onClick={(e) => { e.stopPropagation(); onClick(); }}
      >
        <boxGeometry args={[w, h, d]} />
        <meshStandardMaterial
          map={facade} emissiveMap={facade} emissive={s.glow}
          emissiveIntensity={hover ? 0.5 : 0.22} roughness={0.85}
        />
      </mesh>
      {/* eave beams under the roofline */}
      <mesh position={[0, h - 0.05, d / 2 + 0.12]}><boxGeometry args={[w + 0.3, 0.14, 0.24]} /><meshStandardMaterial color="#241a12" roughness={1} /></mesh>
      <mesh position={[0, h - 0.05, -d / 2 - 0.12]}><boxGeometry args={[w + 0.3, 0.14, 0.24]} /><meshStandardMaterial color="#241a12" roughness={1} /></mesh>
      {/* pitched roof — shingled now */}
      <mesh position={[0, h + w * 0.22, 0]} rotation={[0, Math.PI / 4, 0]} castShadow>
        <coneGeometry args={[w * 0.82, w * 0.55, 4]} />
        <meshStandardMaterial map={roofTex} roughness={0.9} />
      </mesh>
      {/* chimney with an ember-lit mouth (emissive — no light) */}
      <group position={[chimneySide * w * 0.26, h + w * 0.34, -d * 0.12]}>
        <mesh castShadow><boxGeometry args={[0.36, 1.1, 0.36]} /><meshStandardMaterial color="#3a3630" roughness={1} /></mesh>
        <mesh position={[0, 0.58, 0]}><boxGeometry args={[0.46, 0.12, 0.46]} /><meshStandardMaterial color="#2a2620" /></mesh>
        <mesh position={[0, 0.56, 0]}><boxGeometry args={[0.2, 0.06, 0.2]} /><meshBasicMaterial color="#ff8a3a" transparent opacity={0.5} toneMapped={false} /></mesh>
      </group>
      {/* hard black cornice — the "thick black border" of the 2D style, in 3D */}
      <mesh position={[0, h + 0.06, 0]}>
        <boxGeometry args={[w + 0.18, 0.16, d + 0.18]} />
        <meshStandardMaterial color={INK} />
      </mesh>
      {/* hanging door lantern — an emissive cage where the lamp light already lived */}
      <group position={[w * 0.4, 1.9, d * 0.5 + 0.32]}>
        <mesh position={[0, 0.22, -0.14]} rotation={[0.5, 0, 0]}><cylinderGeometry args={[0.025, 0.025, 0.4, 5]} /><meshStandardMaterial color="#241a12" /></mesh>
        <mesh><boxGeometry args={[0.16, 0.22, 0.16]} /><meshStandardMaterial color="#241a12" roughness={1} /></mesh>
        <mesh><boxGeometry args={[0.1, 0.15, 0.1]} /><meshBasicMaterial color={s.glow} toneMapped={false} /></mesh>
      </group>
      {/* trim-colored lamp by the door — SAME light as Ship 1, now with a visible source */}
      <pointLight position={[w * 0.4, 1.6, d * 0.5 + 0.4]} color={s.glow} intensity={hover ? 3 : 1.4} distance={7} />
      {/* per-style crown detail + doorstep clutter */}
      <BuildingDetail id={b.id} w={w} h={h} d={d} s={s} />
      {/* name plate */}
      <Html center position={[0, h + w * 0.55 + 0.6, 0]} zIndexRange={[16, 8]} style={{ pointerEvents: "none" }}>
        <div style={plateStyle(s.trim, hover)}>{name}</div>
      </Html>
    </group>
  );
}

// Per-building silhouette detail — what makes each structure
// identifiable from across the courtyard, PLUS its doorstep
// clutter (barrels, crates, an anvil, a notice board…). All
// seeded, all static, none of it casts shadows (budget law).
function BuildingDetail({ id, w, h, d, s }) {
  if (id === "forge") {
    return (
      <group>
        <mesh position={[w * 0.28, h + w * 0.5, 0]} castShadow>
          <cylinderGeometry args={[0.28, 0.34, 1.6, 8]} />
          <meshStandardMaterial color="#2a2018" />
        </mesh>
        <pointLight position={[w * 0.28, h + w * 0.5 + 0.9, 0]} color="#ff7a3a" intensity={2.2} distance={5} />
        {/* anvil + quench barrel by the door */}
        <group position={[-w * 0.42, 0.28, d * 0.5 + 0.7]}>
          <mesh><boxGeometry args={[0.3, 0.3, 0.5]} /><meshStandardMaterial color="#2c2c30" metalness={0.5} roughness={0.5} /></mesh>
          <mesh position={[0, 0.2, 0.16]}><boxGeometry args={[0.24, 0.1, 0.28]} /><meshStandardMaterial color="#3a3a40" metalness={0.6} roughness={0.4} /></mesh>
          <mesh position={[0, -0.14, 0]}><boxGeometry args={[0.36, 0.14, 0.4]} /><meshStandardMaterial color="#241a12" /></mesh>
        </group>
        <mesh position={[-w * 0.42 + 0.7, 0.3, d * 0.5 + 0.7]}>
          <cylinderGeometry args={[0.26, 0.24, 0.6, 9]} />
          <meshStandardMaterial color="#3a2a1a" roughness={1} />
        </mesh>
      </group>
    );
  }
  if (id === "garden") {
    // crystal flowers on the roof + a bed of glowing buds at the door
    return (
      <group>
        {[[-0.4, 0.2], [0.3, -0.3], [0, 0.4]].map(([ox, oz], i) => (
          <mesh key={i} position={[ox * w, h + 0.5, oz * d]} rotation={[0.2, i, 0]}>
            <octahedronGeometry args={[0.28, 0]} />
            <meshStandardMaterial color={s.trim} emissive={s.glow} emissiveIntensity={0.6} transparent opacity={0.85} />
          </mesh>
        ))}
        {[[-0.6, 0], [0.6, 0.15], [-0.2, 0.3], [0.35, -0.1]].map(([ox, oz], i) => (
          <group key={`bed${i}`} position={[ox * w * 0.6, 0.1, d * 0.5 + 0.5 + oz]}>
            <mesh position={[0, 0.1, 0]}><cylinderGeometry args={[0.02, 0.03, 0.2, 5]} /><meshStandardMaterial color="#2c4a2c" /></mesh>
            <mesh position={[0, 0.24, 0]}><sphereGeometry args={[0.06, 6, 6]} /><meshBasicMaterial color={s.glow} toneMapped={false} /></mesh>
          </group>
        ))}
      </group>
    );
  }
  if (id === "trainer" || id === "registrar") {
    // a slim spire + a banner hanging from the eave
    return (
      <group>
        <mesh position={[0, h + w * 0.55 + 0.3, 0]} castShadow>
          <coneGeometry args={[0.14, 1.0, 6]} />
          <meshStandardMaterial color={s.trim} emissive={s.glow} emissiveIntensity={0.4} />
        </mesh>
        <group position={[-w * 0.32, h - 0.9, d / 2 + 0.1]}>
          <mesh><planeGeometry args={[0.6, 1.5]} /><meshStandardMaterial color={s.wall} side={THREE.DoubleSide} roughness={1} /></mesh>
          <mesh position={[0, 0, 0.005]}><planeGeometry args={[0.44, 1.34]} /><meshStandardMaterial color={s.trim} emissive={s.glow} emissiveIntensity={0.25} side={THREE.DoubleSide} roughness={1} /></mesh>
          <mesh position={[0, 0.78, 0]}><boxGeometry args={[0.7, 0.06, 0.06]} /><meshStandardMaterial color="#241a12" /></mesh>
        </group>
      </group>
    );
  }
  if (id === "marketplace") {
    // hanging sign board + stall awning with a goods table and crates
    return (
      <group>
        <mesh position={[w * 0.5 + 0.2, h * 0.6, 0]}>
          <boxGeometry args={[0.1, 0.7, 0.9]} />
          <meshStandardMaterial color={s.trim} emissive={s.glow} emissiveIntensity={0.3} toneMapped={false} />
        </mesh>
        <group position={[w * 0.55, 0, d * 0.34]}>
          <mesh position={[0.5, 0.75, 0.5]}><cylinderGeometry args={[0.04, 0.05, 1.5, 5]} /><meshStandardMaterial color="#241a12" /></mesh>
          <mesh position={[0.5, 0.75, -0.5]}><cylinderGeometry args={[0.04, 0.05, 1.5, 5]} /><meshStandardMaterial color="#241a12" /></mesh>
          <mesh position={[0.25, 1.55, 0]} rotation={[0, 0, 0.35]}><boxGeometry args={[1.3, 0.05, 1.3]} /><meshStandardMaterial color={s.trim} roughness={1} /></mesh>
          <mesh position={[0.45, 0.5, 0]}><boxGeometry args={[0.8, 0.08, 1.1]} /><meshStandardMaterial color="#3a2a1a" roughness={1} /></mesh>
          <mesh position={[0.35, 0.64, 0.25]}><sphereGeometry args={[0.09, 7, 6]} /><meshStandardMaterial color="#a84c3a" roughness={1} /></mesh>
          <mesh position={[0.55, 0.64, -0.15]}><sphereGeometry args={[0.08, 7, 6]} /><meshStandardMaterial color="#c9a84c" roughness={1} /></mesh>
          <mesh position={[0.35, 0.64, -0.35]}><sphereGeometry args={[0.07, 7, 6]} /><meshStandardMaterial color="#5a7a3a" roughness={1} /></mesh>
        </group>
        <mesh position={[-w * 0.5, 0.28, d * 0.5 + 0.6]} rotation={[0, 0.4, 0]}>
          <boxGeometry args={[0.5, 0.5, 0.5]} />
          <meshStandardMaterial color="#4a3520" roughness={1} />
        </mesh>
        <mesh position={[-w * 0.5 + 0.35, 0.2, d * 0.5 + 0.95]} rotation={[0, 0.9, 0]}>
          <boxGeometry args={[0.36, 0.36, 0.36]} />
          <meshStandardMaterial color="#3f2d1c" roughness={1} />
        </mesh>
      </group>
    );
  }
  if (id === "bulletin") {
    // a pinned-notice board leaning by the door
    return (
      <group position={[w * 0.42, 0, d * 0.5 + 0.4]} rotation={[0, -0.3, 0]}>
        <mesh position={[-0.4, 0.7, 0]}><cylinderGeometry args={[0.04, 0.05, 1.4, 5]} /><meshStandardMaterial color="#241a12" /></mesh>
        <mesh position={[0.4, 0.7, 0]}><cylinderGeometry args={[0.04, 0.05, 1.4, 5]} /><meshStandardMaterial color="#241a12" /></mesh>
        <mesh position={[0, 0.95, 0]}><boxGeometry args={[1.0, 0.7, 0.06]} /><meshStandardMaterial color="#3a2a1a" roughness={1} /></mesh>
        {[[-0.28, 1.05], [0.05, 0.9], [0.3, 1.08]].map(([ox, oy], i) => (
          <mesh key={i} position={[ox, oy, 0.04]} rotation={[0, 0, (i - 1) * 0.12]}>
            <planeGeometry args={[0.2, 0.26]} />
            <meshStandardMaterial color="#e9ddc4" roughness={1} />
          </mesh>
        ))}
      </group>
    );
  }
  return null;
}

// THE BONFIRE — a stone ring with a live flame column and rising
// embers. It's the rest point and the literal center of the Fold.
// ENV-1: layered flame (outer cone + white-hot core), charred
// ground disc, seeded irregular ring stones, log benches facing
// the fire, and two banner poles. Same single light as Ship 1.
function Bonfire({ x, z, onClick, name }) {
  const flame = useRef();
  const core = useRef();
  const embers = useRef();
  const [hover, setHover] = useState(false);
  const emberData = useMemo(() => {
    const R = mulberry32(321);
    return Array.from({ length: 24 }, (_, i) => ({
      a: R() * Math.PI * 2, r: R() * 0.6, y: R() * 2.4, spd: 1 + R() * 1.5, k: i,
    }));
  }, []);
  const ringStones = useMemo(() => {
    const R = mulberry32(654);
    return Array.from({ length: 12 }, (_, i) => ({
      a: (i / 12) * Math.PI * 2,
      sw: 0.45 + R() * 0.2, sh: 0.55 + R() * 0.3,
      tx: (R() - 0.5) * 0.2, tz: (R() - 0.5) * 0.15,
      tone: `rgb(${52 + Math.floor(R() * 16)},${48 + Math.floor(R() * 12)},${42 + Math.floor(R() * 10)})`,
      k: i,
    }));
  }, []);
  useFrame((state, dt) => {
    const t = state.clock.elapsedTime;
    if (flame.current) {
      flame.current.scale.y = 1 + Math.sin(t * 6) * 0.15;
      flame.current.scale.x = 1 + Math.cos(t * 5) * 0.08;
      flame.current.material.emissiveIntensity = 1.6 + Math.sin(t * 8) * 0.4;
    }
    if (core.current) {
      core.current.scale.y = 1 + Math.sin(t * 7 + 1) * 0.2;
      core.current.material.opacity = 0.8 + Math.sin(t * 9) * 0.15;
    }
    if (embers.current) {
      embers.current.children.forEach((m, i) => {
        const e = emberData[i];
        m.position.y += e.spd * dt;
        if (m.position.y > 3) m.position.y = 0.2;
        m.material.opacity = Math.max(0, 1 - m.position.y / 3);
      });
    }
  });
  return (
    <group position={[x, 0, z]}
      onPointerOver={(e) => { e.stopPropagation(); setHover(true); document.body.style.cursor = "pointer"; }}
      onPointerOut={() => { setHover(false); document.body.style.cursor = "auto"; }}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
    >
      {/* charred earth under the fire */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, Y_COBBLE + 0.005, 0]}>
        <circleGeometry args={[2.1, 24]} />
        <meshStandardMaterial color="#171310" roughness={1} />
      </mesh>
      {/* stone ring — seeded irregular stones now */}
      {ringStones.map((st) => (
        <mesh key={st.k} position={[Math.cos(st.a) * 1.5, st.sh / 2, Math.sin(st.a) * 1.5]} rotation={[st.tx, -st.a, st.tz]} castShadow>
          <boxGeometry args={[st.sw, st.sh, 0.5]} />
          <meshStandardMaterial color={st.tone} roughness={1} />
        </mesh>
      ))}
      {/* log benches around the fire — the Fold gathers here */}
      {[0.9, 2.5, 4.2].map((a, i) => (
        <group key={`bench${i}`} position={[Math.cos(a) * 2.9, 0, Math.sin(a) * 2.9]} rotation={[0, -a + Math.PI / 2, 0]}>
          <mesh position={[0, 0.32, 0]} rotation={[0, 0, Math.PI / 2]} castShadow><cylinderGeometry args={[0.16, 0.16, 1.7, 7]} /><meshStandardMaterial color="#3a2a1a" roughness={1} /></mesh>
          <mesh position={[-0.6, 0.12, 0]}><cylinderGeometry args={[0.1, 0.12, 0.24, 6]} /><meshStandardMaterial color="#241a12" /></mesh>
          <mesh position={[0.6, 0.12, 0]}><cylinderGeometry args={[0.1, 0.12, 0.24, 6]} /><meshStandardMaterial color="#241a12" /></mesh>
        </group>
      ))}
      {/* two banner poles flanking the square */}
      {[-1, 1].map((sgn) => (
        <group key={`pole${sgn}`} position={[sgn * 3.6, 0, -2.6]}>
          <mesh position={[0, 1.8, 0]} castShadow><cylinderGeometry args={[0.06, 0.09, 3.6, 6]} /><meshStandardMaterial color="#241a12" roughness={1} /></mesh>
          <mesh position={[sgn * 0.36, 2.8, 0]}><planeGeometry args={[0.64, 1.3]} /><meshStandardMaterial color="#8a2e2e" emissive="#e8a33d" emissiveIntensity={0.12} side={THREE.DoubleSide} roughness={1} /></mesh>
          <mesh position={[sgn * 0.36, 3.5, 0]}><boxGeometry args={[0.76, 0.06, 0.06]} /><meshStandardMaterial color="#141019" /></mesh>
        </group>
      ))}
      {/* logs */}
      <mesh position={[0, 0.35, 0]} rotation={[0, 0.4, 0.1]}><cylinderGeometry args={[0.14, 0.14, 1.6, 6]} /><meshStandardMaterial color="#2a1c10" /></mesh>
      <mesh position={[0, 0.35, 0]} rotation={[0, -0.5, -0.1]}><cylinderGeometry args={[0.14, 0.14, 1.6, 6]} /><meshStandardMaterial color="#2a1c10" /></mesh>
      <mesh position={[0, 0.32, 0]} rotation={[0, 1.4, 0.06]}><cylinderGeometry args={[0.12, 0.12, 1.5, 6]} /><meshStandardMaterial color="#221608" /></mesh>
      {/* flame — outer body + white-hot core */}
      <mesh ref={flame} position={[0, 1.1, 0]}>
        <coneGeometry args={[0.5, 1.6, 8]} />
        <meshStandardMaterial color="#ff8a2a" emissive="#ff6a1a" emissiveIntensity={1.8} transparent opacity={0.92} toneMapped={false} />
      </mesh>
      <mesh ref={core} position={[0, 0.9, 0]}>
        <coneGeometry args={[0.26, 0.9, 7]} />
        <meshBasicMaterial color="#ffe9b0" transparent opacity={0.85} toneMapped={false} depthWrite={false} />
      </mesh>
      <pointLight position={[0, 1.4, 0]} color="#ff7a2a" intensity={hover ? 6 : 4} distance={16} />
      {/* embers */}
      <group ref={embers}>
        {emberData.map((e) => (
          <mesh key={e.k} position={[Math.cos(e.a) * e.r, 0.2 + e.y, Math.sin(e.a) * e.r]}>
            <sphereGeometry args={[0.05, 4, 4]} />
            <meshBasicMaterial color="#ffb24d" transparent opacity={0.9} toneMapped={false} />
          </mesh>
        ))}
      </group>
      <Html center position={[0, 3.2, 0]} zIndexRange={[16, 8]} style={{ pointerEvents: "none" }}>
        <div style={plateStyle("#e8a33d", hover)}>{name}</div>
      </Html>
    </group>
  );
}

// A doorstep pad you can see and click for each building — the
// LOGICAL position stays on the grid; only the art has volume.
function Pad({ b, color, onClick }) {
  const [x, , z] = worldPos(b.x, b.y);
  const [hover, setHover] = useState(false);
  return (
    <mesh
      rotation={[-Math.PI / 2, 0, 0]} position={[x, Y_PAD, z + (b.ry || 2) * UNIT * 0.5]}
      onPointerOver={(e) => { e.stopPropagation(); setHover(true); }}
      onPointerOut={() => setHover(false)}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
    >
      <circleGeometry args={[UNIT * 0.6, 20]} />
      <meshStandardMaterial color={color} transparent opacity={hover ? 0.55 : 0.28} emissive={color} emissiveIntensity={hover ? 0.5 : 0.2} />
    </mesh>
  );
}

// ── PORTALS — the two exits. ENV-1: carved stone arch (painted
// runes climbing the posts, glowing via emissiveMap), swirling
// double-plane fill, capstone + footings. Same light as Ship 1.
function Portal({ p, onClick }) {
  const ring = useRef();
  const swirl = useRef();
  const [x, , z] = worldPos(p.x, p.y);
  const [hover, setHover] = useState(false);
  const runeTex = useMemo(() => canvasTex(`portal_post_env1|${p.id}`, 32, 128, (g, w, h) => {
    g.fillStyle = "#2a2620"; g.fillRect(0, 0, w, h);
    const R = mulberry32(hashId(p.id) + 7);
    for (let i = 0; i < 60; i++) { g.fillStyle = `rgba(0,0,0,${0.1 + R() * 0.15})`; g.fillRect(R() * w, R() * h, 2, 3 + R() * 5); }
    // climbing runes
    g.strokeStyle = hexA(p.color, 0.85); g.lineWidth = 2;
    for (let i = 0; i < 6; i++) {
      const y = 12 + i * 18, cx2 = w / 2 + (R() - 0.5) * 8;
      g.beginPath();
      g.moveTo(cx2 - 5, y); g.lineTo(cx2 + 5, y + (R() > 0.5 ? 6 : -6));
      g.moveTo(cx2, y - 4); g.lineTo(cx2, y + 8);
      if (R() > 0.5) { g.moveTo(cx2 - 4, y + 8); g.lineTo(cx2 + 4, y + 8); }
      g.stroke();
    }
  }), [p.id, p.color]);
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (ring.current) {
      const s = 1 + Math.sin(t * 2) * 0.06;
      ring.current.scale.set(s, s, s);
    }
    if (swirl.current) swirl.current.rotation.z = t * 0.6;
  });
  return (
    <group position={[x, 0, z]}
      onPointerOver={(e) => { e.stopPropagation(); setHover(true); document.body.style.cursor = "pointer"; }}
      onPointerOut={() => { setHover(false); document.body.style.cursor = "auto"; }}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
    >
      {/* carved archway posts + footings + capstone */}
      <mesh position={[-1.5, 1.6, 0]} castShadow><boxGeometry args={[0.44, 3.2, 0.44]} /><meshStandardMaterial map={runeTex} emissiveMap={runeTex} emissive={p.color} emissiveIntensity={0.35} roughness={1} /></mesh>
      <mesh position={[1.5, 1.6, 0]} castShadow><boxGeometry args={[0.44, 3.2, 0.44]} /><meshStandardMaterial map={runeTex} emissiveMap={runeTex} emissive={p.color} emissiveIntensity={0.35} roughness={1} /></mesh>
      <mesh position={[-1.5, 0.12, 0]}><boxGeometry args={[0.7, 0.24, 0.7]} /><meshStandardMaterial color="#3a3630" roughness={1} /></mesh>
      <mesh position={[1.5, 0.12, 0]}><boxGeometry args={[0.7, 0.24, 0.7]} /><meshStandardMaterial color="#3a3630" roughness={1} /></mesh>
      <mesh position={[0, 3.3, 0]} castShadow><boxGeometry args={[3.4, 0.44, 0.44]} /><meshStandardMaterial color="#2a2620" roughness={1} /></mesh>
      <mesh position={[0, 3.62, 0]}><boxGeometry args={[3.7, 0.2, 0.56]} /><meshStandardMaterial color={INK} /></mesh>
      {/* shimmering portal fill + rotating inner swirl */}
      <mesh ref={ring} position={[0, 1.7, 0]}>
        <planeGeometry args={[2.6, 3.0]} />
        <meshBasicMaterial color={p.color} transparent opacity={hover ? 0.55 : 0.35} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
      </mesh>
      <mesh ref={swirl} position={[0, 1.7, 0.02]}>
        <ringGeometry args={[0.4, 1.05, 24, 1, 0, Math.PI * 1.4]} />
        <meshBasicMaterial color="#ffffff" transparent opacity={0.16} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
      </mesh>
      <pointLight position={[0, 1.7, 0.4]} color={p.color} intensity={hover ? 4 : 2.2} distance={10} />
      <Html center position={[0, 4.1, 0]} zIndexRange={[18, 10]} style={{ pointerEvents: "none" }}>
        <div style={plateStyle(p.color, hover)}>{p.label}</div>
      </Html>
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// LAYER 5 · ACTORS — villagers (idle) and the player (you drive).
// ────────────────────────────────────────────────────────────

// Villager — ENV-1 gorgeous-lite pass on the folk of the Fold:
// painted robe texture (no flat cones), a hood that SITS OVER the
// head (robe-read law — draping, not hanging), sleeved arms with
// hands, a cord belt, and a small signature prop per villager so
// each reads at silhouette distance. Idle breathe unchanged.
function Villager({ v, onClick }) {
  const ref = useRef();
  const [x, , z] = worldPos(v.x, v.y);
  const [hover, setHover] = useState(false);
  const name = NPC_NAME[v.id] || v.id;
  const tone = useMemo(() => {
    const hues = { maerie: "#c9a84c", sigfer: "#8fb8d8", vel: "#a86a4a" };
    return hues[v.id] || "#b0a080";
  }, [v.id]);
  const robeTex = useMemo(() => robeTexture(tone), [tone]);
  useFrame((state) => {
    if (ref.current) ref.current.position.y = 0.02 + Math.sin(state.clock.elapsedTime * 2 + x) * 0.03;
  });
  return (
    <group position={[x, 0, z]}
      onPointerOver={(e) => { e.stopPropagation(); setHover(true); document.body.style.cursor = "pointer"; }}
      onPointerOut={() => { setHover(false); document.body.style.cursor = "auto"; }}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
    >
      <group ref={ref}>
        {/* robe — painted cloth skirt cone + soft chest mass */}
        <mesh position={[0, 0.62, 0]} castShadow>
          <coneGeometry args={[0.44, 1.24, 10]} />
          <meshStandardMaterial map={robeTex} roughness={1} />
        </mesh>
        <mesh position={[0, 1.08, 0]}>
          <sphereGeometry args={[0.3, 10, 9]} />
          <meshStandardMaterial map={robeTex} roughness={1} />
        </mesh>
        {/* cord belt */}
        <mesh position={[0, 0.86, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.31, 0.025, 6, 14]} />
          <meshStandardMaterial color="#241a12" roughness={1} />
        </mesh>
        {/* sleeved arms */}
        <mesh position={[-0.3, 0.98, 0.08]} rotation={[0.25, 0, 0.55]}>
          <cylinderGeometry args={[0.07, 0.1, 0.5, 7]} />
          <meshStandardMaterial map={robeTex} roughness={1} />
        </mesh>
        <mesh position={[0.3, 0.98, 0.08]} rotation={[0.25, 0, -0.55]}>
          <cylinderGeometry args={[0.07, 0.1, 0.5, 7]} />
          <meshStandardMaterial map={robeTex} roughness={1} />
        </mesh>
        {/* hands */}
        <mesh position={[-0.4, 0.78, 0.16]}><sphereGeometry args={[0.06, 7, 6]} /><meshStandardMaterial color="#e8d8b8" roughness={1} /></mesh>
        <mesh position={[0.4, 0.78, 0.16]}><sphereGeometry args={[0.06, 7, 6]} /><meshStandardMaterial color="#e8d8b8" roughness={1} /></mesh>
        {/* head — shadowed under the hood */}
        <mesh position={[0, 1.55, 0.02]} castShadow><sphereGeometry args={[0.24, 12, 12]} /><meshStandardMaterial color="#d8c8a8" roughness={1} /></mesh>
        {/* hood — draping over the head */}
        <mesh position={[0, 1.62, -0.06]}>
          <sphereGeometry args={[0.31, 10, 10, 0, Math.PI * 2, 0, Math.PI / 1.7]} />
          <meshStandardMaterial map={robeTex} roughness={1} side={THREE.DoubleSide} />
        </mesh>
        {/* hood shadow across the face */}
        <mesh position={[0, 1.6, 0.16]} rotation={[0.5, 0, 0]}>
          <circleGeometry args={[0.19, 10]} />
          <meshBasicMaterial color="#0c0a08" transparent opacity={0.4} depthWrite={false} />
        </mesh>
        {/* signature prop per villager */}
        {v.id === "maerie" && ( // her market basket
          <group position={[0.44, 0.72, 0.14]}>
            <mesh><cylinderGeometry args={[0.12, 0.09, 0.14, 8]} /><meshStandardMaterial color="#8a6a3a" roughness={1} /></mesh>
            <mesh position={[0, 0.1, 0]} rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[0.1, 0.015, 5, 10, Math.PI]} /><meshStandardMaterial color="#6a4e28" /></mesh>
            <mesh position={[0.03, 0.08, 0]}><sphereGeometry args={[0.045, 6, 6]} /><meshStandardMaterial color="#a84c3a" /></mesh>
          </group>
        )}
        {v.id === "sigfer" && ( // the trainer's practice sword on his back
          <group position={[0, 1.05, -0.3]} rotation={[0.15, 0, 0.7]}>
            <mesh><boxGeometry args={[0.05, 0.9, 0.05]} /><meshStandardMaterial color="#8a8a90" metalness={0.5} roughness={0.4} /></mesh>
            <mesh position={[0, -0.42, 0]}><boxGeometry args={[0.2, 0.05, 0.07]} /><meshStandardMaterial color="#241a12" /></mesh>
            <mesh position={[0, -0.52, 0]}><cylinderGeometry args={[0.03, 0.03, 0.16, 6]} /><meshStandardMaterial color="#3a2a1a" /></mesh>
          </group>
        )}
        {v.id === "vel" && ( // a gnarled walking staff with an ember tip
          <group position={[-0.42, 0.9, 0.1]} rotation={[0, 0, 0.08]}>
            <mesh><cylinderGeometry args={[0.03, 0.045, 1.7, 6]} /><meshStandardMaterial color="#3a2a1a" roughness={1} /></mesh>
            <mesh position={[0, 0.9, 0]}><sphereGeometry args={[0.06, 7, 6]} /><meshBasicMaterial color="#ffb24d" toneMapped={false} /></mesh>
          </group>
        )}
      </group>
      <GroundBlob />
      <Html center position={[0, 2.2, 0]} zIndexRange={[14, 6]} style={{ pointerEvents: "none" }}>
        <div style={plateStyle(tone, hover, true)}>{name}</div>
      </Html>
    </group>
  );
}

// §4.18 — GroundBlob / PlayerBody / BeingMesh / TravelerMesh now
// live in fold3dKit.js (imported above). Aliased for the local
// call sites below.
const GroundBlob = KitGroundBlob;

// ── The controllable actor: local movement (WASD/arrows), 8-way,
// clamped to the courtyard, writes its live position into a ref
// the camera reads. This is the ONLY game state this file owns,
// and it's local-only — exactly what the 2D world layer did.
function Player({ beingId, tier, keysRef, followRef }) {
  const group = useRef();
  const pos = useRef(new THREE.Vector3(...worldPos(SPAWN.x, SPAWN.y)));
  const facing = useRef({ x: 0, y: 1 });
  const [moving, setMoving] = useState(false);

  const bounds = useMemo(() => ({
    minX: -HALF_X + UNIT, maxX: HALF_X - UNIT,
    minZ: -HALF_Z + UNIT, maxZ: HALF_Z - UNIT,
  }), []);

  useFrame((_, dt) => {
    const k = keysRef.current;
    let dx = 0, dz = 0;
    if (k.up) dz -= 1; if (k.down) dz += 1;
    if (k.left) dx -= 1; if (k.right) dx += 1;
    const isMoving = dx !== 0 || dz !== 0;
    if (isMoving !== moving) setMoving(isMoving);
    if (isMoving) {
      const len = Math.hypot(dx, dz) || 1;
      const SPEED = 7.5;
      const nx = pos.current.x + (dx / len) * SPEED * dt;
      const nz = pos.current.z + (dz / len) * SPEED * dt;
      pos.current.x = Math.max(bounds.minX, Math.min(bounds.maxX, nx));
      pos.current.z = Math.max(bounds.minZ, Math.min(bounds.maxZ, nz));
      facing.current = { x: dx / len, y: dz / len };
    }
    if (group.current) group.current.position.copy(pos.current);
    followRef.current = pos.current;   // ← the entire Follow Cam hook
  });

  return (
    <group ref={group}>
      <PlayerBody beingId={beingId} tier={tier} facing={facing.current} moving={moving} />
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// LAYER 6 · CAMERA — THE FOLLOW. Goal-based lerp, user always
// wins. The rig is the playbook's rig; only presets changed.
// ────────────────────────────────────────────────────────────
function CameraRig({ followRef, mode }) {
  const controls = useRef();
  const { camera } = useThree();
  const goal = useRef({ pos: new THREE.Vector3(0, 22, 24), tgt: new THREE.Vector3(0, 0, 0), active: true });

  useFrame(() => {
    const g = goal.current;
    if (mode === "follow" && followRef.current) {
      const p = followRef.current;
      g.pos.set(p.x + 0.001, 12, p.z + 15);   // over-shoulder-ish, high enough to read the courtyard
      g.tgt.set(p.x, 1.2, p.z);
      g.active = true;
    } else if (mode === "bird") {
      g.pos.set(0, 44, 30); g.tgt.set(0, 0, 0); g.active = true;
    }
    if (g.active && controls.current) {
      camera.position.lerp(g.pos, 0.06);
      controls.current.target.lerp(g.tgt, 0.08);
      controls.current.update();
      if (mode !== "follow" && camera.position.distanceTo(g.pos) < 0.3) g.active = false;
    }
  });

  return (
    <OrbitControls
      ref={controls} enableDamping dampingFactor={0.08}
      minDistance={6} maxDistance={90} maxPolarAngle={Math.PI / 2.12}
      onStart={() => { if (mode !== "follow") goal.current.active = false; }}
    />
  );
}

// (plateStyle now imported from fold3dKit — §4.18)

// ────────────────────────────────────────────────────────────
// LAYER 7 · UI CHROME — plain React outside <Canvas>.
// ────────────────────────────────────────────────────────────
const btn = (active) => ({
  padding: "8px 14px",
  background: active ? GOLD : "rgba(20,16,25,0.9)",
  color: active ? "#1a1410" : VELLUM,
  border: `2px solid ${GOLD}`,
  borderRadius: 8,
  fontFamily: "Georgia, serif",
  fontWeight: 700,
  fontSize: 13,
  cursor: "pointer",
  boxShadow: `0 3px 0 ${INK}`,
  letterSpacing: 0.5,
});

// ── THE SCENE ────────────────────────────────────────────────
function Scene({ beingId, tier, keysRef, mode, players = [], onEnterBuilding, onEnterShop, onForge, onEnterNPC, onPierre, onEnterDungeon, gateEntry }) {
  const followRef = useRef(new THREE.Vector3(...worldPos(SPAWN.x, SPAWN.y)));
  const buildings = useMemo(() => buildingList(false), []);
  const villagers = useMemo(() => villagerList(false), []);
  const portals = useMemo(() => portalList(false), []);

  // Route a building click through the SAME dispatch the 2D shell uses.
  const clickBuilding = useCallback((id) => {
    if (id === "forge") return onForge && onForge();
    if (id === "marketplace") return onEnterShop && onEnterShop(id);
    return onEnterBuilding && onEnterBuilding(BUILD_SCREEN[id] || id);
  }, [onForge, onEnterShop, onEnterBuilding]);

  const clickPortal = useCallback((p) => {
    if (p.id === "pierre") return onPierre && onPierre();
    if (p.id === "gate") return onEnterDungeon && onEnterDungeon(gateEntry);
  }, [onPierre, onEnterDungeon, gateEntry]);

  return (
    <>
      <color attach="background" args={["#0c0a14"]} />
      <fog attach="fog" args={["#1a1428", 34, 92]} />

      {/* lighting rig — high ambient for readability + a warm key +
          a violet rim (the cheat that makes silhouettes read). */}
      <ambientLight intensity={0.55} />
      <hemisphereLight args={["#3a2f5a", "#1a140a", 0.5]} />
      <directionalLight
        position={[24, 40, 18]} intensity={0.9} castShadow
        shadow-mapSize-width={2048} shadow-mapSize-height={2048}
        shadow-camera-left={-HALF_X - 6} shadow-camera-right={HALF_X + 6}
        shadow-camera-top={HALF_Z + 6} shadow-camera-bottom={-HALF_Z - 6}
        shadow-camera-far={140}
      />
      <directionalLight position={[-30, 20, -24]} intensity={0.35} color="#b388ff" />

      <Sky />
      <Ground />
      <Grass />
      <Perimeter />
      <CobbleRing />
      <Paths buildings={buildings} />
      <LampPosts buildings={buildings} />
      <Motes />

      {/* buildings + their clickable doorstep pads */}
      {buildings.map((b) => (
        <group key={b.id}>
          <Building b={b} onClick={() => clickBuilding(b.id)} />
          {b.id !== "bonfire" && <Pad b={b} color={b.color} onClick={() => clickBuilding(b.id)} />}
        </group>
      ))}

      {/* portals */}
      {portals.map((p) => <Portal key={p.id} p={p} onClick={() => clickPortal(p)} />)}

      {/* villagers */}
      {villagers.map((v) => <Villager key={v.id} v={v} onClick={() => onEnterNPC && onEnterNPC(v.id)} />)}

      {/* the player */}
      <Player beingId={beingId} tier={tier} keysRef={keysRef} followRef={followRef} />

      {/* other travelers — presence-driven (Batch 3D-6). The 2D
          shell keeps owning the Firebase listener; this just draws
          the array it's handed, lerped like everything else. */}
      {players.map((p) => <RemotePlayer key={p.sid} p={p} />)}

      <CameraRig followRef={followRef} mode={mode} />
    </>
  );
}

// ── Remote traveler — a presence row given a body. Grid coords
// come off the wire (the 2D shell's tile grid, same numbers as
// oldFoldGeometry), so worldPos maps them straight in. Lerp keeps
// the 5s-heartbeat tile steps from teleporting. Their being walks
// if they have one; name tag overhead, hard-outlined (kit Label).
function RemotePlayer({ p }) {
  const grp = useRef();
  const cur = useRef(new THREE.Vector3(...worldPos(p.x ?? 0, p.y ?? 0)));
  const target = useRef(new THREE.Vector3(...worldPos(p.x ?? 0, p.y ?? 0)));
  const moving = useRef(false);
  useEffect(() => {
    const [tx, , tz] = worldPos(p.x ?? 0, p.y ?? 0);
    target.current.set(tx, 0, tz);
  }, [p.x, p.y]);
  useFrame(() => {
    const before = cur.current.clone();
    cur.current.lerp(target.current, 0.12);
    moving.current = cur.current.distanceTo(before) > 0.004;
    if (grp.current) grp.current.position.copy(cur.current);
  });
  const facing = { x: p.facingX ?? 0, y: p.facingY ?? 1 };
  return (
    <group ref={grp}>
      <PlayerBody
        beingId={p.beingId || null}
        tier={p.beingTier || null}
        facing={facing}
        moving={moving.current}
      />
      <KitGroundBlob r={0.45} opacity={0.28} />
      <Label position={[0, 2.5, 0]} text={p.name || "traveler"} accent={GOLD} small />
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// THE EXPORT — same-props contract with Plaza's world layer.
// ────────────────────────────────────────────────────────────
export default function OldFold3D({
  // identity (optional — falls back to the traveler)
  character = null,
  // callbacks — MIRROR Plaza exactly
  onEnterBuilding = () => {},
  onEnterShop = () => {},
  onForge = () => {},
  onPierre = () => {},
  onEnterNPC = () => {},
  onEnterDungeon = () => {},
  onBack = () => {},
  // the Gate's destination entry (Plaza passes GATE_DESTINATION)
  gateEntry = null,
  // other travelers in the square — [{ sid, name, x, y, facingX,
  // facingY, beingId, beingTier }] straight off the presence wire.
  players = [],
}) {
  const beingId = character?.beingId || null;
  const tier = useMemo(() => {
    if (!character) return null;
    const { level } = levelForXp(character.xp || 0);
    return tierForLevel(level);
  }, [character]);

  const [mode, setMode] = useState("follow");
  const keysRef = useRef({ up: false, down: false, left: false, right: false });

  // keyboard — WASD + arrows drive the body
  useEffect(() => {
    const set = (e, v) => {
      const k = e.key.toLowerCase();
      const K = keysRef.current;
      if (k === "w" || k === "arrowup") K.up = v;
      else if (k === "s" || k === "arrowdown") K.down = v;
      else if (k === "a" || k === "arrowleft") K.left = v;
      else if (k === "d" || k === "arrowright") K.right = v;
      else return;
      e.preventDefault();
    };
    const down = (e) => set(e, true);
    const up = (e) => set(e, false);
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, []);

  return (
    <div style={{ position: "fixed", inset: 0, background: "#0c0a14" }}>
      <Canvas shadows camera={{ position: [0, 22, 24], fov: 50 }} dpr={[1, 2]}>
        <Scene
          beingId={beingId} tier={tier} keysRef={keysRef} mode={mode}
          players={players}
          onEnterBuilding={onEnterBuilding} onEnterShop={onEnterShop}
          onForge={onForge} onEnterNPC={onEnterNPC} onPierre={onPierre}
          onEnterDungeon={onEnterDungeon} gateEntry={gateEntry}
        />
      </Canvas>

      {/* mobile: movement d-pad (no combat in the village, so no
          action buttons — TouchControls only shows on touch devices) */}
      <TouchControls keysRef={keysRef} accent={GOLD} />

      {/* ── chrome: back + camera modes + hint ── */}
      <div style={{ position: "absolute", top: 14, left: 14, display: "flex", gap: 8, zIndex: 20 }}>
        <button style={btn(false)} onClick={onBack}>← Back</button>
      </div>
      <div style={{ position: "absolute", top: 14, right: 14, display: "flex", gap: 8, zIndex: 20 }}>
        <button style={btn(mode === "follow")} onClick={() => setMode("follow")}>Follow</button>
        <button style={btn(mode === "bird")} onClick={() => setMode("bird")}>Overhead</button>
      </div>
      <div style={{
        position: "absolute", bottom: 16, left: "50%", transform: "translateX(-50%)",
        background: "rgba(20,16,25,0.82)", border: `1px solid ${GOLD}`, borderRadius: 10,
        padding: "7px 16px", color: VELLUM, fontFamily: "Georgia, serif", fontSize: 12.5,
        letterSpacing: 0.5, zIndex: 20, textShadow: "0 1px 2px #000",
      }}>
        WASD / arrows to walk · click a building or villager to enter · drag to orbit
      </div>
    </div>
  );
}
